/**
 * Manager activity-log scope — keep in lockstep with
 * erp_api/routes/controllers/logAccess.ts (department ∩ reporting tree).
 */

import { isCallerAboveEmployeeInReportingLine } from 'pages/Records/employeeManagerRules';
import { unwrapLogWithLegacy } from './logFormat';

export type ManagerLogAccessRecord = {
  id?: string | null
  kind?: string | null
  name?: string | null
  parent_id?: string | null
  parentId?: string | null
  data?: Record<string, unknown> | null
  fields?: Record<string, unknown> | null
}

export type ManagerLogAccessStaff = {
  id?: string | number | null
  username?: string | null
  email?: string | null
}

export type ManagerLogAccessContext = {
  viewerEmployeeId: string
  viewerDepartmentId: string
  managerByEmployeeId: Record<string, string>
  actorTokenToEmployeeId: Map<string, string>
  employeeDepartmentId: Map<string, string>
}

type ManagerLogShape = {
  message?: string | null
  created_by?: string | null
}

const rowFields = (row: ManagerLogAccessRecord): Record<string, unknown> => {
  if (row.fields && typeof row.fields === 'object') return row.fields;
  if (row.data && typeof row.data === 'object') return row.data;
  return {};
};

const rowParentId = (row: ManagerLogAccessRecord): string => (
  String(row.parent_id || row.parentId || '').trim()
);

const normEmail = (value: unknown): string => String(value || '').trim().toLowerCase();

const setActorToken = (
  map: Map<string, string>,
  token: string | null | undefined,
  employeeId: string,
) => {
  const key = String(token || '').trim();
  if (!key || !employeeId) return;
  map.set(key, employeeId);
  map.set(key.replace(/\s+/g, '').toLowerCase(), employeeId);
  map.set(key.toLowerCase(), employeeId);
};

/** Employee department from fields.department or designation → department parent. */
export const resolveEmployeeDepartmentIdFromRecords = (
  records: ManagerLogAccessRecord[],
  employeeId: string,
): string | null => {
  const id = String(employeeId || '').trim();
  if (!id) return null;
  const employee = records.find(
    (row) => String(row.id || '') === id && String(row.kind || '') === 'employee',
  );
  if (!employee) return null;
  const data = rowFields(employee);
  const deptFromField = String(data.department || '').trim();
  if (deptFromField) return deptFromField;
  const designationId = rowParentId(employee);
  if (!designationId) return null;
  const designation = records.find(
    (row) => String(row.id || '') === designationId && String(row.kind || '') === 'designation',
  );
  const parentDept = designation ? rowParentId(designation) : '';
  return parentDept || null;
};

export const buildManagerLogAccessContext = (
  records: ManagerLogAccessRecord[],
  staff: ManagerLogAccessStaff[],
  viewerStaffId?: string | null,
  viewerUsername?: string | null,
): ManagerLogAccessContext | null => {
  const rows = records || [];
  const managerByEmployeeId: Record<string, string> = {};
  const employeeDepartmentId = new Map<string, string>();
  const actorTokenToEmployeeId = new Map<string, string>();
  const emailToEmployeeId = new Map<string, string>();

  rows.forEach((row) => {
    const id = String(row.id || '').trim();
    if (!id) return;
    if (String(row.kind || '') !== 'employee') return;
    const data = rowFields(row);
    managerByEmployeeId[id] = String(data.manager || '').trim();
    const deptId = resolveEmployeeDepartmentIdFromRecords(rows, id);
    if (deptId) employeeDepartmentId.set(id, deptId);
    setActorToken(actorTokenToEmployeeId, id, id);
    const email = normEmail(data.email);
    if (email) {
      emailToEmployeeId.set(email, id);
      setActorToken(actorTokenToEmployeeId, email, id);
    }
    const name = String(row.name || data.name || '').trim();
    if (name) setActorToken(actorTokenToEmployeeId, name, id);
    const staffCode = String(data.employeeNumber || data.code || '').trim();
    if (staffCode) setActorToken(actorTokenToEmployeeId, staffCode, id);
  });

  (staff || []).forEach((u) => {
    const email = normEmail(u.email);
    const employeeId = email ? emailToEmployeeId.get(email) : null;
    if (!employeeId) return;
    if (u.id != null && String(u.id) !== '') {
      setActorToken(actorTokenToEmployeeId, String(u.id), employeeId);
    }
    if (u.username) setActorToken(actorTokenToEmployeeId, u.username, employeeId);
  });

  const viewerStaff = String(viewerStaffId || '').trim();
  const viewerUser = String(viewerUsername || '').trim();
  let viewerEmployeeId = '';
  if (viewerStaff) {
    viewerEmployeeId = actorTokenToEmployeeId.get(viewerStaff)
      || actorTokenToEmployeeId.get(viewerStaff.toLowerCase())
      || '';
  }
  if (!viewerEmployeeId && viewerUser) {
    viewerEmployeeId = actorTokenToEmployeeId.get(viewerUser)
      || actorTokenToEmployeeId.get(viewerUser.replace(/\s+/g, '').toLowerCase())
      || actorTokenToEmployeeId.get(viewerUser.toLowerCase())
      || '';
  }
  if (!viewerEmployeeId) return null;

  const viewerDepartmentId = employeeDepartmentId.get(viewerEmployeeId) || '';
  if (!viewerDepartmentId) return null;

  return {
    viewerEmployeeId,
    viewerDepartmentId,
    managerByEmployeeId,
    actorTokenToEmployeeId,
    employeeDepartmentId,
  };
};

const extractActorTokens = (message: string): string[] => (
  unwrapLogWithLegacy('actor', message || '')
);

const getLogActorIdentity = (log: ManagerLogShape): string | null => {
  if (log.created_by != null && String(log.created_by) !== '') {
    return String(log.created_by);
  }
  return extractActorTokens(log.message || '')[0] || null;
};

const logMentionsUser = (
  message: string,
  session: { staffId?: string | null, username?: string | null },
): boolean => {
  const tokens = extractActorTokens(message);
  if (!tokens.length) return false;
  const staffId = session.staffId != null && String(session.staffId) !== ''
    ? String(session.staffId)
    : '';
  if (staffId && tokens.some((t) => t === staffId)) return true;
  const username = (session.username || '').trim();
  if (username && tokens.some((t) => t.toLowerCase() === username.toLowerCase())) return true;
  return false;
};

const resolveLogActorEmployeeId = (
  log: ManagerLogShape,
  ctx: ManagerLogAccessContext,
): string | null => {
  const actor = getLogActorIdentity(log);
  if (!actor) return null;
  return ctx.actorTokenToEmployeeId.get(String(actor))
    || ctx.actorTokenToEmployeeId.get(String(actor).toLowerCase())
    || ctx.actorTokenToEmployeeId.get(String(actor).replace(/\s+/g, '').toLowerCase())
    || null;
};

/** Manager: own logs, or same department + author below them in the reporting tree. */
export const canViewManagerScopedActivityLog = (
  log: ManagerLogShape,
  opts: {
    staffId?: string | null
    username?: string | null
    managerContext: ManagerLogAccessContext | null
  },
): boolean => {
  const sessionActor = { staffId: opts.staffId || '', username: opts.username || '' };
  const isOwn = logMentionsUser(log.message || '', sessionActor)
    || Boolean(opts.staffId && String(log.created_by || '') === String(opts.staffId));

  const ctx = opts.managerContext;
  if (!ctx) return isOwn;

  const actorEmployeeId = resolveLogActorEmployeeId(log, ctx);
  if (isOwn || (actorEmployeeId && actorEmployeeId === ctx.viewerEmployeeId)) {
    return true;
  }
  if (!actorEmployeeId) return false;

  // Author must be in the manager's department and below them in the reporting tree.
  const actorDepartmentId = ctx.employeeDepartmentId.get(actorEmployeeId) || '';
  if (!actorDepartmentId || actorDepartmentId !== ctx.viewerDepartmentId) return false;

  return isCallerAboveEmployeeInReportingLine(
    ctx.viewerEmployeeId,
    actorEmployeeId,
    ctx.managerByEmployeeId[actorEmployeeId],
    ctx.managerByEmployeeId,
  );
};
